import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { unifiedNFCService } from '@/services/nfc/unifiedNFCService';
import { NFCCardData } from '@/services/nfc/nfcServiceInterface';
import { platformDetector } from '@/services/platformDetector';
import { showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconX,
  IconCreditCard,
  IconDeviceMobile,
  IconDeviceDesktop,
} from '@tabler/icons-react';

interface NFCContextValue {
  isConnected: boolean;
  isWriting: boolean;
  lastCard: NFCCardData | null;
  clearLastCard: () => void;
  platform: ReturnType<typeof platformDetector.getPlatformInfo>;
  nfcServiceType: 'websocket' | 'web-nfc' | 'mock';
  requestWriteCard: (type: number, entityId: number) => Promise<void>;
  needsManualScan: boolean; // Android端末で手動スキャンが必要かどうか
  startManualScan: () => Promise<void>; // 手動スキャン開始
}

const NFCContext = createContext<NFCContextValue | undefined>(undefined);

export const useNFC = () => {
  const context = useContext(NFCContext);
  if (!context) {
    throw new Error('useNFC must be used within NFCProvider');
  }
  return context;
};

interface NFCProviderProps {
  children: React.ReactNode;
}

export const NFCProvider: React.FC<NFCProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [lastCard, setLastCard] = useState<NFCCardData | null>(null);
  const [platform] = useState(() => platformDetector.getPlatformInfo());
  const [nfcServiceType, setNfcServiceType] = useState<'websocket' | 'web-nfc' | 'mock'>('mock');
  const [needsManualScan, setNeedsManualScan] = useState(false); // 手動スキャンが必要かどうか

  useEffect(() => {
    // プラットフォーム情報をログ出力

    // NFCが利用可能でない場合は早期リターン
    if (!platformDetector.isNFCAvailable()) {
      console.warn('NFC is not available on this platform');
      // const limitations = platformDetector.getPlatformLimitations();
      // NFC未対応でも他の機能に影響を与えないように通知を表示しない
      // showNotification({
      //   title: 'NFC未対応',
      //   message: limitations.join('\n'),
      //   color: 'orange',
      //   icon: platform.isAndroid ? <IconDeviceMobile /> : <IconDeviceDesktop />,
      //   autoClose: false,
      // });
      return;
    }

    // 統合NFCサービスに接続
    const connectService = async () => {
      try {
        await unifiedNFCService.connect();
        setNfcServiceType(unifiedNFCService.getServiceType());
        // 接続完了後に明示的に状態を設定（connected イベントの取りこぼし対策）
        setIsConnected(unifiedNFCService.isConnected());

        // Android端末の場合、権限確認のみ行い、自動スキャンは開始しない
        if (platform.isAndroid && unifiedNFCService.getServiceType() === 'web-nfc') {
          // Web NFC APIが利用可能かチェック
          if ('NDEFReader' in window) {
            // 権限状態を確認
            try {
              // 権限チェックのみ（実際のスキャンは開始しない）
              const permissionStatus = await navigator.permissions.query({
                name: 'nfc' as PermissionName,
              });

              if (permissionStatus.state === 'denied') {
                // 権限が拒否されている場合
                setNeedsManualScan(true);
                showNotification({
                  title: 'NFC使用許可が必要です',
                  message: '画面下部のボタンをタップしてNFCの使用を許可してください',
                  color: 'yellow',
                  icon: <IconCreditCard />,
                  autoClose: false,
                });
              } else if (permissionStatus.state === 'prompt') {
                // 権限がまだ要求されていない場合
                setNeedsManualScan(true);
                showNotification({
                  title: 'NFCの準備完了',
                  message: 'NFCカードをかざすと読み取りを開始します',
                  color: 'blue',
                  icon: <IconCreditCard />,
                  autoClose: 3000,
                });
              } else {
                // 権限が許可されている場合
                showNotification({
                  title: 'NFC準備完了',
                  message: 'NFCカードをかざしてください',
                  color: 'green',
                  icon: <IconCheck />,
                  autoClose: 3000,
                });
              }
            } catch (error) {
              console.warn('NFC permission check failed:', error);
              // 権限チェックが失敗した場合も手動スキャンボタンを表示
              setNeedsManualScan(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to connect NFC service:', error);
      }
    };

    connectService();

    // イベントハンドラの登録
    const unsubscribeConnected = unifiedNFCService.on('connected', () => {
      // 既に接続済みの場合は通知をスキップ（リロード時の重複防止）
      if (!isConnected) {
        setIsConnected(true);
        const serviceType = unifiedNFCService.getServiceType();
        setNfcServiceType(serviceType);

        // ログイン画面と公式登録フォームでは接続通知を表示しない
        const isLoginPage = window.location.pathname === '/login';
        const isPublicRegisterPage = window.location.pathname === '/register';
        if (!isLoginPage && !isPublicRegisterPage) {
          showNotification({
            title: 'NFC接続完了',
            message: `NFCリーダーに接続しました (${serviceType === 'web-nfc' ? 'Android' : 'Windows'})`,
            color: 'green',
            icon: <IconCheck />,
            autoClose: 3000,
          });
        }
      }
    });

    const unsubscribeDisconnected = unifiedNFCService.on('disconnected', () => {
      setIsConnected(false);
      showNotification({
        title: 'NFC切断',
        message: 'NFCリーダーとの接続が切断されました',
        color: 'orange',
        icon: <IconX />,
      });
    });

    // NFCカード検出
    const unsubscribeNfcDetected = unifiedNFCService.on('nfc_detected', (event) => {
      const cardData = event.data as NFCCardData;
      setLastCard(cardData);

      // 書き込みモード中は通知しない
      const inWriteMode = isWriting || unifiedNFCService.isWriteMode();
      if (!inWriteMode) {
        showNotification({
          title: 'カード検出',
          message: `UID: ${cardData.uid}`,
          color: 'blue',
          icon: <IconCreditCard />,
          autoClose: 3000,
        });
      }
    });

    // カード書き込み完了
    const unsubscribeCardWritten = unifiedNFCService.on('card_written', () => {
      setIsWriting(false);
    });

    // エラー処理
    const unsubscribeError = unifiedNFCService.on('error', (event) => {
      if (event.context === 'write') {
        setIsWriting(false);
      }
    });

    // 権限拒否処理（Android用）
    const unsubscribePermissionDenied = unifiedNFCService.on('permission_denied', () => {
      showNotification({
        title: 'NFC権限が必要です',
        message: 'ブラウザの設定でNFC権限を許可してください',
        color: 'red',
        autoClose: false,
      });
    });

    // 初期接続状態の確認
    setIsConnected(unifiedNFCService.isConnected());
    if (unifiedNFCService.isConnected()) {
      setNfcServiceType(unifiedNFCService.getServiceType());
    }

    // クリーンアップ
    return () => {
      unsubscribeConnected();
      unsubscribeDisconnected();
      unsubscribeNfcDetected();
      unsubscribeCardWritten();
      unsubscribeError();
      unsubscribePermissionDenied();
      unifiedNFCService.disconnect();
    };
  }, []);

  const clearLastCard = useCallback(() => {
    setLastCard(null);
  }, []);

  const requestWriteCard = useCallback(async (type: number, entityId: number) => {
    setIsWriting(true);
    try {
      await unifiedNFCService.requestWriteCard(type, entityId);
    } catch (error) {
      setIsWriting(false);
      throw error;
    }
  }, []);

  const startManualScan = useCallback(async () => {
    try {
      if (unifiedNFCService.startManualScan) {
        await unifiedNFCService.startManualScan();
        setNeedsManualScan(false); // 手動スキャンが成功したらボタンを非表示
        showNotification({
          title: 'NFCスキャン開始',
          message: 'NFCカードをかざしてください',
          color: 'green',
          icon: <IconCreditCard />,
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      console.error('Manual scan failed:', error);
      if (error.name !== 'NotAllowedError') {
        // 権限エラー以外の場合はエラー通知
        showNotification({
          title: 'スキャンエラー',
          message: error.message || 'NFCスキャンの開始に失敗しました',
          color: 'red',
          icon: <IconX />,
        });
      }
      throw error;
    }
  }, []);

  const value: NFCContextValue = {
    isConnected,
    isWriting,
    lastCard,
    clearLastCard,
    platform,
    nfcServiceType,
    requestWriteCard,
    needsManualScan,
    startManualScan,
  };

  return <NFCContext.Provider value={value}>{children}</NFCContext.Provider>;
};
